import { useState, useEffect } from 'react';
import { Plus, Check, X, Server, Loader2 } from 'lucide-react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';

interface TemplateGrupo {
    id: string;
    nome: string;
    descricao?: string;
    ativo: boolean;
}

interface TemplateMultiParte {
    id: string;
    nome: string;
    descricao?: string;
    tipoMaquina?: string;
    grupoId?: string;
    partes: any[];
    ativo: boolean;
}

interface SeletorGrupoTipoProps {
    projetoId: string;
    onTipoSelecionado: (tipo: TemplateMultiParte | null) => void;
    tipoSelecionadoId?: string | null;
}

export function SeletorGrupoTipo({ projetoId, onTipoSelecionado, tipoSelecionadoId }: SeletorGrupoTipoProps) {
    const { t } = useI18n();

    // Estados para Grupos
    const [grupos, setGrupos] = useState<TemplateGrupo[]>([]);
    const [grupoSelecionado, setGrupoSelecionado] = useState<TemplateGrupo | null>(null);
    const [carregandoGrupos, setCarregandoGrupos] = useState(false);
    const [modoCriarGrupo, setModoCriarGrupo] = useState(false);
    const [novoGrupoNome, setNovoGrupoNome] = useState('');

    // Estados para Tipos (MultiPartes)
    const [tipos, setTipos] = useState<TemplateMultiParte[]>([]);
    const [tipoSelecionado, setTipoSelecionado] = useState<TemplateMultiParte | null>(null);
    const [carregandoTipos, setCarregandoTipos] = useState(false);
    const [modoCriarTipo, setModoCriarTipo] = useState(false);
    const [novoTipoNome, setNovoTipoNome] = useState('');

    // Estado geral
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const semGrupos = !carregandoGrupos && !erro && grupos.length === 0;

    // Carregar grupos ao iniciar
    useEffect(() => {
        if (projetoId) {
            carregarGrupos();
        }
    }, [projetoId]);

    // Carregar tipos quando um grupo for selecionado
    useEffect(() => {
        if (grupoSelecionado) {
            carregarTipos(grupoSelecionado.id);
        } else {
            setTipos([]);
            setTipoSelecionado(null);
            onTipoSelecionado(null);
        }
    }, [grupoSelecionado]);

    // Sincronizar seleção externa
    useEffect(() => {
        if (tipoSelecionadoId && tipos.length > 0) {
            const tipo = tipos.find(t => t.id === tipoSelecionadoId);
            if (tipo && (!tipoSelecionado || tipoSelecionado.id !== tipo.id)) {
                setTipoSelecionado(tipo);
            }
        }
    }, [tipoSelecionadoId, tipos]);

    const carregarGrupos = async () => {
        setCarregandoGrupos(true);
        setErro(null);
        try {
            const resp = await apiService.listarGrupos({ projetoId, limit: 100, ativo: true });
            if (resp.success && resp.data) {
                setGrupos(resp.data);
            } else {
                setGrupos([]);
                setErro(t('templates.seletor.erroCarregarGrupos'));
            }
        } catch (err) {
            setGrupos([]);
            setErro(t('templates.seletor.erroConexaoGrupos'));
        } finally {
            setCarregandoGrupos(false);
        }
    };

    const carregarTipos = async (grupoId: string) => {
        setCarregandoTipos(true);
        setErro(null);
        try {
            const resp = await apiService.listarTemplateMultiPartes({ projetoId, grupoId, limit: 100, ativo: true });
            if (resp.success && Array.isArray(resp.data)) {
                setTipos(resp.data);
            } else {
                setErro(t('templates.seletor.erroCarregarTipos'));
            }
        } catch (err) {
            setErro(t('templates.seletor.erroConexaoTipos'));
        } finally {
            setCarregandoTipos(false);
        }
    };

    const handleSelecionarGrupo = (grupoId: string) => {
        if (!grupoId) {
            setGrupoSelecionado(null);
            return;
        }
        const grupo = grupos.find(g => g.id === grupoId) || null;
        setGrupoSelecionado(grupo);
    };

    const handleSelecionarTipo = (tipoId: string) => {
        if (!tipoId) {
            setTipoSelecionado(null);
            onTipoSelecionado(null);
            return;
        }
        const tipo = tipos.find(t => t.id === tipoId) || null;
        setTipoSelecionado(tipo);
        onTipoSelecionado(tipo);
    };

    const handleSalvarNovoGrupo = async () => {
        if (!novoGrupoNome.trim()) return;
        setSalvando(true);
        setErro(null);
        try {
            const resp = await apiService.criarGrupo({
                nome: novoGrupoNome.trim(),
                projetoId,
                ativo: true
            });
            if (resp.success && resp.data) {
                await carregarGrupos();
                setNovoGrupoNome('');
                setModoCriarGrupo(false);
                setGrupoSelecionado(resp.data);
            } else {
                setErro(resp.message || t('templates.seletor.erroCriarGrupo'));
            }
        } catch (err) {
            setErro(t('templates.seletor.erroCriarGrupo'));
        } finally {
            setSalvando(false);
        }
    };

    const handleSalvarNovoTipo = async () => {
        if (!novoTipoNome.trim() || !grupoSelecionado) return;
        setSalvando(true);
        setErro(null);
        try {
            const resp = await apiService.criarTemplateMultiParte({
                nome: novoTipoNome.trim(),
                projetoId,
                grupoId: grupoSelecionado.id,
                // Compatibilidade com backend legado que exige ao menos uma parte na criação
                partes: [{ nome: 'Parte 1', riscos: [] }],
                ativo: true
            });
            if (resp.success && resp.data) {
                await carregarTipos(grupoSelecionado.id);
                setNovoTipoNome('');
                setModoCriarTipo(false);
                const novoTipo = resp.data;
                setTipoSelecionado(novoTipo);
                onTipoSelecionado(novoTipo);
            } else {
                setErro(resp.message || t('templates.seletor.erroCriarTipo'));
            }
        } catch (err) {
            setErro(t('templates.seletor.erroCriarTipo'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className="bg-white dark:bg-schmersal-gray-dark border border-gray-200 dark:border-schmersal-gray-darker rounded-xl shadow-sm p-6 mb-8 transition-colors">
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg text-schmersal-blue dark:text-blue-400">
                    <Server className="h-5 w-5" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">{t('templates.seletor.titulo')}</h2>
            </div>

            {erro && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md border border-red-200 dark:border-red-800 text-sm">
                    {erro}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Coluna 1: GRUPO */}
                <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {t('templates.seletor.grupoLabel')} <span className="text-red-500">*</span>
                    </label>

                    {modoCriarGrupo ? (
                        <div className="flex gap-2">
                            <input
                                type="text"
                                autoFocus
                                placeholder={t('templates.seletor.grupoPlaceholder')}
                                value={novoGrupoNome}
                                onChange={(e) => setNovoGrupoNome(e.target.value)}
                                className="flex-1 rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white shadow-sm focus:border-schmersal-blue focus:ring-schmersal-blue sm:text-sm"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSalvarNovoGrupo();
                                    if (e.key === 'Escape') setModoCriarGrupo(false);
                                }}
                                disabled={salvando}
                            />
                            <button
                                type="button"
                                onClick={handleSalvarNovoGrupo}
                                disabled={salvando || !novoGrupoNome.trim()}
                                className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-md disabled:opacity-50 transition-colors"
                                title={t('comum.acao.salvar')}
                            >
                                {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </button>
                            <button
                                type="button"
                                onClick={() => setModoCriarGrupo(false)}
                                disabled={salvando}
                                className="p-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
                                title={t('comum.acao.cancelar')}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <select
                                    value={grupoSelecionado?.id || ''}
                                    onChange={(e) => handleSelecionarGrupo(e.target.value)}
                                    className="w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white shadow-sm focus:border-schmersal-blue focus:ring-schmersal-blue sm:text-sm disabled:opacity-50"
                                    disabled={carregandoGrupos || semGrupos}
                                >
                                    <option value="">
                                        {semGrupos ? t('templates.seletor.grupoIndisponivel') : t('templates.seletor.grupoSelecione')}
                                    </option>
                                    {grupos.map(g => (
                                        <option key={g.id} value={g.id}>{g.nome}</option>
                                    ))}
                                </select>
                                {carregandoGrupos && (
                                    <div className="absolute right-8 top-1/2 -translate-y-1/2">
                                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setModoCriarGrupo(true)}
                                disabled={carregandoGrupos}
                                className="px-3 py-2 bg-schmersal-blue text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm font-medium shadow-sm"
                            >
                                <Plus className="h-4 w-4" /> {t('templates.seletor.grupoNovo')}
                            </button>
                        </div>
                    )}
                    {semGrupos && (
                        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                            <p className="mb-2">{t('templates.seletor.grupoSemCadastro')}</p>
                            <button
                                type="button"
                                onClick={() => setModoCriarGrupo(true)}
                                className="inline-flex items-center gap-1 rounded-md bg-schmersal-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                            >
                                <Plus className="h-3.5 w-3.5" />
                                {t('templates.seletor.grupoCriarPrimeiro')}
                            </button>
                        </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('templates.seletor.grupoAjuda')}
                    </p>
                </div>

                {/* Coluna 2: TIPO */}
                <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {t('templates.seletor.tipoLabel')} <span className="text-red-500">*</span>
                    </label>

                    {modoCriarTipo ? (
                        <div className="flex gap-2">
                            <input
                                type="text"
                                autoFocus
                                placeholder={t('templates.seletor.tipoPlaceholder')}
                                value={novoTipoNome}
                                onChange={(e) => setNovoTipoNome(e.target.value)}
                                className="flex-1 rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white shadow-sm focus:border-schmersal-blue focus:ring-schmersal-blue sm:text-sm"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSalvarNovoTipo();
                                    if (e.key === 'Escape') setModoCriarTipo(false);
                                }}
                                disabled={salvando}
                            />
                            <button
                                type="button"
                                onClick={handleSalvarNovoTipo}
                                disabled={salvando || !novoTipoNome.trim()}
                                className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-md disabled:opacity-50 transition-colors"
                                title={t('comum.acao.salvar')}
                            >
                                {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </button>
                            <button
                                type="button"
                                onClick={() => setModoCriarTipo(false)}
                                disabled={salvando}
                                className="p-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
                                title={t('comum.acao.cancelar')}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <select
                                    value={tipoSelecionado?.id || ''}
                                    onChange={(e) => handleSelecionarTipo(e.target.value)}
                                    className="w-full rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white shadow-sm focus:border-schmersal-blue focus:ring-schmersal-blue sm:text-sm disabled:opacity-50"
                                    disabled={carregandoTipos || !grupoSelecionado}
                                >
                                    <option value="">
                                        {grupoSelecionado
                                            ? t('templates.seletor.tipoSelecione')
                                            : semGrupos
                                                ? t('templates.seletor.tipoCrieGrupo')
                                                : t('templates.seletor.tipoSelecioneGrupo')}
                                    </option>
                                    {tipos.map(t => (
                                        <option key={t.id} value={t.id}>{t.nome}</option>
                                    ))}
                                </select>
                                {carregandoTipos && (
                                    <div className="absolute right-8 top-1/2 -translate-y-1/2">
                                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setModoCriarTipo(true)}
                                disabled={!grupoSelecionado}
                                className="px-3 py-2 bg-schmersal-blue text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm font-medium disabled:opacity-50 shadow-sm"
                            >
                                <Plus className="h-4 w-4" /> {t('templates.seletor.tipoNovo')}
                            </button>
                        </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('templates.seletor.tipoAjuda')}
                    </p>
                </div>
            </div>
        </div>
    );
}
